
import { useEffect, useContext } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import { Breadcrumbs, Button, Grid, GridItem, ThemeContext, useHtmlTag } from '@forma/forma-ui-kit';
import TextBlockForSeo from '@/components/SeoBlocks/TextBlockForSeo';
import BlockTemplate from '@/components/SeoBlocks/BlockTemplate';
import FillDocument from '@/components/SeoBlocks/FillDocument';
import Element from '@/components/SeoBlocks/parts/Element';
import { TryConstructorAndDemo } from '@/components/SeoBlocks/TryConstructorAndDemo';
import ClientWrap from '@/components/ClientWrap';
import SocialMediaButtons from '@/components/SeoBlocks/parts/SocialMediaButtons';
import SignatureBanner from '@/components/SeoBlocks/SignatureBanner';
import DocumentHTML from '@/components/SeoBlocks/DocumentHTML';
import ButtonsBlock from '@/components/blocks/ButtonsBlock/ButtonsBlock';
import Link from '@/components/Link';
import Logo from '@/components/Logo';
import { requests } from '@/helpers/api';

import styles from './filling-template-and-information.module.css';

const FillingTemplateAndInformation = ({
  solutions,
  template,
  seoMain,
  templatesFiltred,
  sides,
  previews,
  onSetDataFromForm,
  breadCrumbsLinks,
  content,
  folder
}) => {
  const { t } = useTranslation();
  const { viewport, lang } = useContext(ThemeContext);
  const { name, description, showHTML, seoName } = template;
  const { content: contentHTML, blocksTree } = content;
  const { renderTemplateFromSeo } = requests;
  const { try_constructor_block } = seoMain;
  const { video } = try_constructor_block;
  const router = useRouter();
  useHtmlTag('script', 'https://usocial.pro/usocial/usocial.js?uid=cd1204b657f67871&v=6.1.5');
  useHtmlTag('script', 'https://yastatic.net/share2/share.js');

  const isPhone = viewport === 'phone';

  const breadcrumbItems = [
    { name: t('homepage'), href: '/templates', as: Link },
  ];

  for (let i = 0; i < breadCrumbsLinks.length; i++) {
    breadcrumbItems.push({
      name: breadCrumbsLinks[i].name,
      href: `/templates/${breadCrumbsLinks[i].path}`,
      as: Link,
    });
  }

  breadcrumbItems.push({ name: name });

  const handleOnClickDowload = async (data) => {
    if (!data.length) return;
    const resultRendered = await renderTemplateFromSeo(template.id, {
      name: template.name,
      sideValues: data,
    }, lang);
    if (resultRendered.id) onSetDataFromForm(resultRendered);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.uSocialShare && !document.querySelector('.uscl-bar')) {
      window.uSocialShare && window.uSocialShare.reInit();
    }
    if (window.uSocialShare && document.querySelector('.uscl-bar')) {
      document.querySelector('.uscl-bar').remove();
      window.uSocialShare.reInit();
    }

    if (window.Ya && document.querySelector('.ya-share2') && window.Ya.share2) {
      window.Ya.share2(document.querySelector('.ya-share2'), {
        content: {
          url: `${process.env.SITE_URL}${router.asPath}`,
        },
      });
    }
  }, [router.asPath]);

  return (
    <>
      <div className="container">
        <div className={styles.breadCrumbs}>
          <Button
            as={Link}
            viewStyle="secondary"
            className={styles.button}
            iconClassName={styles.buttonIcon}
            icon={<ReactSVG src="/icons/chevron-left-small.svg" wrapper="span" />}
            href="/templates"
          >
            {!isPhone && 'Назад'}
          </Button>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <h1 className={styles.title}>{seoName ? seoName : name}</h1>
        <BlockTemplate previews={previews} template={template} />

        <div className={styles.banners}>
          <SignatureBanner name="Конструктор документов" title={<>Создадим <span className="accent">шаблоны</span> для ваших типовых документов</>} />
          <SignatureBanner name="Электронная подпись" title={<>Подписывайте документы <span className="accent">дистанционно</span> с помощью ПЭП через SMS</>} />
        </div>

        <div className={styles.buttons}>
          <Logo className={styles.logo} />
          <ButtonsBlock
            title="Сферы применения"
            items={solutions}
            className={styles.buttonsBlock}
          />
        </div>

        <div className={styles.filling}>
          <div className={styles.fillingTitle}>
            <Trans
              i18nKey="filling_title"
              components={{ underline: <span className={styles.underline} /> }}
            />
            <img src="/icons/file_types/pdf.svg" className={styles.fillingTitleIcon} alt="" />
          </div>
          <div className={styles.fillingSubtitle}>
            <ul>
              <li><Trans i18nKey="filling_subtitle_1" components={{ accent: <span className={styles.accent} /> }} /></li>
              <li>{t('filling_subtitle_2')}</li>
              <li>{t('filling_subtitle_3')}</li>
            </ul>
          </div>
          <ClientWrap>
            <FillDocument sides={sides} blocksTree={blocksTree} onClickDownload={handleOnClickDowload} />
          </ClientWrap>
        </div>
        <div className={styles.exampleTitle}>{t('render_example_title')}</div>
        {showHTML && <DocumentHTML contentHTML={contentHTML} />}
      </div>
      <TryConstructorAndDemo video={video} />
      <div className="container">
        {templatesFiltred.length !== 0 && (
          <div className={styles.templates}>
            <div className={styles.subtitle}>{t('also_suit_you')}</div>
            <Grid columns={3} className={styles.resultBlock} responsive={{ tabletS: 1, phone: 1 }}>
              {templatesFiltred.map((elem) => (
                <GridItem key={elem.id}>
                  <Element
                    type="doc"
                    href={`/templates/${elem.path}`}
                    {...elem}
                  >
                    {elem.name}
                  </Element>
                </GridItem>
              ))}
            </Grid>
          </div>
        )}
        {/* <RequestCall
          title={t('leave_your_contacts')}
          subtitle={t('we_will_set')}
        /> */}
        <TextBlockForSeo>
          <h2 className={styles.seoTitle}>{folder.name}</h2>
          {description && <div dangerouslySetInnerHTML={{ __html: description }}></div>}
          <p>{t('seo_paragraph_template', { title: seoName ? seoName : name, year: new Date().getFullYear() })}</p>
          <SocialMediaButtons />
        </TextBlockForSeo>
      </div>
    </>
  );
};

export default FillingTemplateAndInformation;
